import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Download,
  ExternalLink,
  Images,
  KeyRound,
  LoaderCircle,
  RefreshCw,
  Save,
  Send,
  Sparkles,
  Store,
  UserRound,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { BeforeAfter } from '../components/BeforeAfter';
import { api, ApiClientError } from '../services/api';
import { useDesignerStore } from '../state/session';
import type { UserDesignHistory } from '../types';

const statusLabel: Record<string, string> = {
  PENDING: 'در صف ساخت',
  PROCESSING: 'در حال ساخت',
  COMPLETED: 'تکمیل‌شده',
  FAILED: 'ناموفق',
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('fa-IR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const normalizePhone = (value: string) => {
  const latin = value
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[^\d+]/g, '');
  if (latin.startsWith('+98')) return `0${latin.slice(3)}`;
  if (latin.startsWith('0098')) return `0${latin.slice(4)}`;
  if (latin.startsWith('98') && latin.length === 12) return `0${latin.slice(2)}`;
  return latin;
};

export function UserPanelPage() {
  const userToken = useDesignerStore((state) => state.userToken);
  const setUserToken = useDesignerStore((state) => state.setUserToken);
  const setAccountUser = useDesignerStore((state) => state.setAccountUser);
  const clearUserToken = useDesignerStore((state) => state.clearUserToken);
  const [history, setHistory] = useState<UserDesignHistory | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(Boolean(userToken));
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [authStep, setAuthStep] = useState<'phone' | 'code'>('phone');
  const [authBusy, setAuthBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [mockCode, setMockCode] = useState<string | null>(null);

  const loadHistory = useCallback(async (token: string, requestedPage: number, allowRefresh = true) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.userDesigns(token, requestedPage);
      setHistory(result);
      setAccountUser(result.user);
      setPage(result.pagination.page);
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.status === 401 && allowRefresh) {
        try {
          const refreshed = await api.refreshAccount();
          setUserToken(refreshed.token);
          setAccountUser(refreshed.user);
          const result = await api.userDesigns(refreshed.token, requestedPage);
          setAccountUser(result.user);
          setHistory(result);
          setPage(result.pagination.page);
          return;
        } catch {
          clearUserToken();
          setHistory(null);
          setError('نشست شما پایان یافته است. با شماره موبایل وارد حساب خود شوید.');
          return;
        }
      }
      setError(caught instanceof Error ? caught.message : 'دریافت تاریخچه طراحی‌ها ناموفق بود.');
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  }, [clearUserToken, setAccountUser, setUserToken]);

  useEffect(() => {
    if (userToken) {
      void loadHistory(userToken, page);
      return;
    }
    let cancelled = false;
    void api.refreshAccount()
      .then((result) => {
        if (cancelled) return;
        setUserToken(result.token);
        setAccountUser(result.user);
      })
      .catch(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => { cancelled = true; };
    // Bootstrap authentication from the protected refresh cookie unless the user explicitly logged out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setInterval(() => setResendIn((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendIn]);

  const requestOtp = async () => {
    const normalized = normalizePhone(phone);
    if (!/^09\d{9}$/.test(normalized)) {
      setError('شماره موبایل را با فرمت 09 و 11 رقم وارد کنید.');
      return;
    }
    setAuthBusy(true);
    setError(null);
    try {
      const result = await api.requestAccountOtp(normalized);
      setPhone(normalized);
      setAuthStep('code');
      setResendIn(result.expiresIn);
      setMockCode(result.mockCode ?? null);
      setCode('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'ارسال رمز یک‌بارمصرف ناموفق بود.');
    } finally {
      setAuthBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{4}$/.test(code)) {
      setError('کد چهاررقمی ارسال‌شده را وارد کنید.');
      return;
    }
    setAuthBusy(true);
    setError(null);
    try {
      const result = await api.verifyAccountOtp(phone, code);
      setUserToken(result.token);
      setAccountUser(result.user);
      await loadHistory(result.token, 1, false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تأیید رمز یک‌بارمصرف ناموفق بود.');
    } finally {
      setAuthBusy(false);
    }
  };

  if (initializing) {
    return <main className="user-panel-shell"><section className="account-state-card"><LoaderCircle className="spin" /><p>در حال بررسی ورود امن شما…</p></section></main>;
  }

  if (!userToken) {
    return <main className="user-panel-shell">
      <section className="account-login-card">
        <div className="account-login-intro">
          <span className="account-login-icon"><UserRound /></span>
          <span className="eyebrow">حساب کاربری کارولایت</span>
          <h1>طراحی‌های من</h1>
          <p>با همان شماره‌ای که هنگام طراحی تأیید کرده‌اید وارد شوید؛ تمام طراحی‌های قبلی شما در دسترس خواهد بود.</p>
        </div>
        <div className="account-login-form">
          {error && <div className="notice error" role="alert"><AlertCircle /><span>{error}</span></div>}
          {authStep === 'phone' ? <>
            <label className="field">
              <span>شماره موبایل</span>
              <input inputMode="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && void requestOtp()} placeholder="09123456789" dir="ltr" />
            </label>
            <button className="primary-button wide" disabled={authBusy} onClick={() => void requestOtp()}>
              {authBusy ? <><LoaderCircle className="spin" />در حال ارسال…</> : <><Send />ارسال کد ورود</>}
            </button>
          </> : <>
            <div className="otp-sent-copy">کد ورود به شماره <b dir="ltr">{phone}</b> ارسال شد.</div>
            {mockCode && <div className="mock-otp">کد محیط توسعه: <b>{mockCode}</b></div>}
            <label className="field">
              <span>کد یک‌بارمصرف</span>
              <input inputMode="numeric" autoComplete="one-time-code" maxLength={4} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 4))} onKeyDown={(event) => event.key === 'Enter' && void verifyOtp()} placeholder="ــــ" dir="ltr" />
            </label>
            <button className="primary-button wide" disabled={authBusy || code.length !== 4} onClick={() => void verifyOtp()}>
              {authBusy ? <><LoaderCircle className="spin" />در حال بررسی…</> : <><KeyRound />ورود به حساب</>}
            </button>
            <div className="otp-secondary-actions">
              <button type="button" className="text-button" onClick={() => { setAuthStep('phone'); setCode(''); setError(null); }}>تغییر شماره</button>
              <button type="button" className="text-button" disabled={authBusy || resendIn > 0} onClick={() => void requestOtp()}>{resendIn > 0 ? `ارسال مجدد تا ${resendIn} ثانیه` : 'ارسال مجدد کد'}</button>
            </div>
          </>}
          <Link className="account-start-link" to="/design"><Sparkles />هنوز طراحی ندارید؟ طراحی جدید بسازید</Link>
        </div>
      </section>
    </main>;
  }

  if (loading && !history) {
    return <main className="user-panel-shell"><section className="account-state-card"><LoaderCircle className="spin" /><p>در حال دریافت طراحی‌های شما…</p></section></main>;
  }

  const pagination = history?.pagination;
  return <main className="user-panel-shell">
    <header className="user-panel-heading">
      <div className="account-heading-copy">
        <span className="eyebrow">فضای شخصی شما</span>
        <div className="account-title-row"><span className="account-avatar" aria-hidden="true"><Save /></span><h1>طراحی‌های ذخیره‌شده</h1></div>
        <p>{history ? `${history.user.name} عزیز، خروجی‌ها و محصولات پیشنهادی شما اینجا نگهداری می‌شوند.` : 'خروجی‌ها و محصولات پیشنهادی شما'}</p>
      </div>
      <div className="user-panel-actions">
        <Link className="primary-button account-new-design" to="/design"><Sparkles />طراحی جدید</Link>
        <button className="secondary-button account-refresh" disabled={loading} onClick={() => userToken && void loadHistory(userToken, page)} aria-label="به‌روزرسانی طراحی‌ها" title="به‌روزرسانی"><RefreshCw className={loading ? 'spin' : ''} /></button>
      </div>
    </header>

    {error && <div className="notice error" role="alert"><AlertCircle /><span>{error}</span></div>}

    {history && history.designs.length === 0 ? (
      <section className="account-state-card"><Images /><h2>هنوز طراحی‌ای ثبت نشده است</h2><p>بعد از ساخت اولین تصویر، نتیجه به‌طور خودکار در این صفحه می‌ماند.</p><Link className="primary-button" to="/design">ساخت اولین طراحی</Link></section>
    ) : (
      <>
        <div className="history-summary">نمایش {history?.designs.length ?? 0} طراحی از مجموع {pagination?.total ?? 0} طراحی</div>
        <section className="design-history-grid" aria-label="تاریخچه طراحی‌ها">
          {history?.designs.map((design) => {
            const preview = design.resultImageUrl || design.originalImageUrl;
            return <article className="history-design-card" key={design.id}>
              <div className="history-design-visual">
                {design.originalImageUrl && design.resultImageUrl
                  ? <BeforeAfter before={design.originalImageUrl} after={design.resultImageUrl} />
                  : <div className="history-design-image">{preview ? <img src={preview} alt={`طراحی ثبت‌شده در ${formatDate(design.createdAt)}`} /> : <span><Images /></span>}</div>}
                <b className={`history-status status-${design.status.toLowerCase()}`}>{statusLabel[design.status] || design.status}</b>
              </div>
              <div className="history-design-copy">
                <div className="history-design-meta"><time>{formatDate(design.completedAt || design.createdAt)}</time>{design.originalImageUrl && design.resultImageUrl && <span>برای مقایسه، خط تصویر را جابه‌جا کنید</span>}</div>
                <h2>{[design.roomType, design.style].filter(Boolean).join(' — ') || 'طراحی نورپردازی کارو لایت'}</h2>
                {design.products.length > 0 && <details className="history-products"><summary><span><Store />محصولات این طراحی</span><b>{design.products.length.toLocaleString('fa-IR')}</b></summary><div className="history-product-tags">{design.products.map((product) => <a key={product.id} href={product.storeUrl} target="_blank" rel="noreferrer">{product.name}<ExternalLink /></a>)}</div></details>}
                {design.resultImageUrl && <div className="history-design-actions"><a className="secondary-button compact-icon-action" href={design.resultImageUrl} target="_blank" rel="noreferrer" aria-label="مشاهده کامل طراحی" title="مشاهده کامل"><ExternalLink /></a><a className="primary-button compact-icon-action" href={design.resultImageUrl} download={`karolight-${design.id}.jpg`} aria-label="دانلود طراحی" title="دانلود"><Download /></a></div>}
              </div>
            </article>;
          })}
        </section>
        {pagination && pagination.totalPages > 1 && <nav className="pagination" aria-label="صفحه‌بندی طراحی‌ها">
          <button className="secondary-button" disabled={loading || page <= 1} onClick={() => userToken && void loadHistory(userToken, page - 1)}>صفحه قبل</button>
          <span>صفحه {pagination.page} از {pagination.totalPages}</span>
          <button className="secondary-button" disabled={loading || page >= pagination.totalPages} onClick={() => userToken && void loadHistory(userToken, page + 1)}>صفحه بعد</button>
        </nav>}
      </>
    )}
  </main>;
}
